import React, { useState, useEffect } from 'react';

const UserSettingsModal = ({ 
  isOpen, 
  onClose, 
  settings, 
  onSave 
}) => {
  const [localSettings, setLocalSettings] = useState(settings);

  // settings prop이 변경될 때마다 localSettings 업데이트
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const handleSchoolLevelChange = (schoolLevel) => {
    setLocalSettings({
      ...localSettings,
      defaultSchoolLevel: schoolLevel,
      // 학교급이 실제로 변경된 경우에만 학년 초기화
      defaultGrade: localSettings.defaultSchoolLevel === schoolLevel ? localSettings.defaultGrade : ''
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800">내 정보 기억하기</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-6">
          {/* 기본 학교급 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              기본 학교급
            </label>
            <select 
              value={localSettings.defaultSchoolLevel}
              onChange={(e) => handleSchoolLevelChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">선택하세요</option>
              <option value="elementary">초등학교</option>
              <option value="middle">중학교</option>
              <option value="high">고등학교</option>
              <option value="advanced">심화과정</option>
            </select>
          </div>

          {/* 기본 학년 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              기본 학년
            </label>
            <select 
              value={localSettings.defaultGrade}
              onChange={(e) => setLocalSettings({...localSettings, defaultGrade: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!localSettings.defaultSchoolLevel}
            >
              <option value="">선택하세요</option>
              {localSettings.defaultSchoolLevel === 'elementary' && (
                <>
                  <option value="3">3학년</option>
                  <option value="4">4학년</option>
                  <option value="5">5학년</option>
                  <option value="6">6학년</option>
                </>
              )}
              {localSettings.defaultSchoolLevel === 'middle' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {localSettings.defaultSchoolLevel === 'high' && (
                <>
                  <option value="1">1학년</option>
                  <option value="2">2학년</option>
                  <option value="3">3학년</option>
                </>
              )}
              {localSettings.defaultSchoolLevel === 'advanced' && (
                <>
                  <option value="1">1단계</option>
                  <option value="2">2단계</option>
                  <option value="3">3단계</option>
                </>
              )}
            </select>
          </div>

          {/* 기본 구글 시트 URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              기본 구글 시트 URL
              <span className="text-xs text-gray-500 ml-1">(선택사항)</span>
            </label>
            <input
              type="url"
              value={localSettings.defaultSheetUrl}
              onChange={(e) => setLocalSettings({...localSettings, defaultSheetUrl: e.target.value})}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              설정하면 모든 템플릿에서 자동으로 적용됩니다
            </p>
          </div>

          {/* 설명 */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-blue-700 font-medium">💡 이 설정은 어떻게 사용되나요?</p>
                <ul className="text-xs text-blue-600 mt-1 space-y-1">
                  <li>• 단어카드, 퀴즈 생성시 기본값으로 자동 입력</li>
                  <li>• 필요시 개별 템플릿에서 수정 가능</li>
                  <li>• 브라우저에 안전하게 저장됩니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            저장하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsModal;